import { useMemo, useCallback, useState, useRef } from 'preact/hooks';

export type PressOptions = {
  isDisabled?: boolean;
  isRepeat?: boolean;
  repeatDelay?: number;
  repeatInterval?: number;
};

const DefaultSettings = {
  isDisabled: false,
  isRepeat: false,
  repeatDelay: 500,
  repeatInterval: 40
};

/**
 * Returns a click handler that can make a target element either clickable or keyboard pressable.
 * This is intended for elements that do not generate a click for kb interaction (non-buttons).
 * (For those, usePressClick is recommended.)`
 *
 * @param onPressHandler function
 * @param isRepeat boolean
 * @param repeatInterval number number of ms between events for mouse and touch
 * @param repeatDelay number number of ms before repeat events initiates
 * @param isDisabled boolean
 * @returns
 */

export function usePress(
  onPressHandler: (event: Event) => void,
  settings: PressOptions = DefaultSettings
): { pressProps: Record<string, any> } {
  const [isPressed, setIsPressed] = useState<boolean>(false);
  const intervalID = useRef<ReturnType<typeof setInterval>>();
  const isRepeating = useRef<boolean>(false);
  const mergedSettings = useMemo(() => {
    return { ...DefaultSettings, ...settings };
  }, [settings]);

  const repeatHandler = useCallback(
    (event: Event) => {
      if (isRepeating.current) {
        onPressHandler(event);
      }
      intervalID.current = setInterval(onPressHandler, mergedSettings.repeatInterval, event); // @HTMLUpdateOK
    },
    [mergedSettings.repeatInterval, onPressHandler]
  );

  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (
        (event.code === 'Space' || event.code === 'Enter') &&
        (mergedSettings.isRepeat || !event.repeat)
      ) {
        event.preventDefault();
        setIsPressed(true);
        if (mergedSettings.isRepeat) {
          onPressHandler(event);
        }
      }
    },
    [mergedSettings.isRepeat, onPressHandler, setIsPressed]
  );

  const onKeyUp = useCallback(
    (event: KeyboardEvent) => {
      if (isPressed && (event.code === 'Space' || event.code === 'Enter')) {
        setIsPressed(false);
        event.preventDefault();
        if (!mergedSettings.isRepeat) {
          onPressHandler(event);
        }
      }
    },
    [mergedSettings.isRepeat, onPressHandler, setIsPressed, isPressed]
  );

  const onBlur = useCallback(() => {
    setIsPressed(false);
  }, [setIsPressed]);

  const onPointerDown = useCallback(
    (event: PointerEvent) => {
      setIsPressed(true);
      isRepeating.current = true;
      event.preventDefault();
      onPressHandler(event);
      intervalID.current = setTimeout(repeatHandler, mergedSettings.repeatDelay, event);
    },
    [mergedSettings, onPressHandler, intervalID, repeatHandler, setIsPressed]
  );

  const onPointerUp = useCallback(
    (event: PointerEvent) => {
      if (intervalID.current) {
        isRepeating.current = false;
        clearInterval(intervalID.current);
        intervalID.current = undefined;
      } else {
        if (isPressed) {
          onPressHandler(event);
        }
      }
      setIsPressed(false);
    },
    [intervalID, onPressHandler, isPressed]
  );

  // Keep this to permit automated invocation of target via click event
  // Need to ignore this for manual invocation whereby browser generates click
  // pointerdown/pointerup/click -> ignore click
  // keydown/keyup/click -> ignore click
  // click without pointerup or keyup -> ignore
  const onClick = useCallback(
    (event: Event) => {
      // There are use cases where pressable/clickable components (eg. Buttons) are nested inside of other
      // components that also process bubbling click events (eg. Collapsible). In order to avoid conflicting
      // processing of clicks (eg. in order to avoid the click both activating a Button as well as toggling the
      // ancestor Collapsible), we stop propagation here. That way, only the child component handles the
      // click and the ancestor component is oblivious to the fact that the click even occurred.
      event.stopPropagation();
      if (intervalID.current) {
        onPressHandler(event);
      }
    },
    [onPressHandler, intervalID]
  );

  const onClickSingle = useCallback(
    (event: Event) => {
      // See note above about why propagation is stopped: to prevent nested component bubbling clicks issue
      event.stopPropagation();
      // This usePress is intended for non-button components that do no generate emulated clicks for kb.
      // So if a click is received, it's coming from a pointer interaction and should be honored.
      // No need to enforce down/up so just use default click html behavior.
      onPressHandler(event);
    },
    [onPressHandler]
  );

  const onPointerOut = useCallback(() => {
    if (intervalID.current) {
      clearInterval(intervalID.current);
    }
  }, [intervalID]);

  const onPointerCancel = useCallback(() => {
    if (intervalID.current) {
      clearInterval(intervalID.current);
    }
  }, [intervalID]);

  const onContextMenu = useCallback((event: PointerEvent) => {
    event.preventDefault();
  }, []);

  const pressProps = mergedSettings.isDisabled
    ? {}
    : mergedSettings.isRepeat
    ? {
        onPointerUp,
        onPointerDown,
        onPointerOut,
        onPointerCancel,
        onKeyDown,
        onKeyUp,
        onContextMenu,
        onClick,
        onBlur
      }
    : {
        onKeyDown,
        onKeyUp,
        onClick: onClickSingle
      };

  return {
    pressProps
  };
}
